import React, { useCallback, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import api from '../../../services/api';
import { fetcher } from '../../../services/fetcher';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { Button } from '../../../components/ui/Button';
import ConfirmationDialog from '../../../components/ui/ConfirmationDialog';
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../../../components/ui/Modal';
import { cn } from '../../../lib/utils';
import {
  activateExcelDocumentTemplate,
  deleteExcelDocumentTemplate,
  downloadExcelDocumentTemplateOriginal,
  downloadExcelDocumentTemplatePreviewFilled,
  publishExcelDocumentTemplate,
  updateExcelDocumentTemplateMapping,
  uploadExcelDocumentTemplate,
} from '../../../services/mutations';
import { MappingEditor, type MappingEditorHandle } from './MappingEditor';
import {
  CheckCircleIcon,
  CheckIcon,
  DocumentTextIcon,
  DownloadIcon,
  EyeIcon,
  FilterIcon,
  InfoIcon,
  PencilIcon,
  StarIcon,
  TableIcon,
  TrashIcon,
  UploadCloudIcon,
  ViewColumnsIcon,
  XIcon,
} from '../../../components/ui/Icons';

const STAGES = [
  'REQUISITION',
  'RFQ',
  'QUOTATION',
  'EVALUATION',
  'ORDER',
  'DELIVERY',
  'INVOICE',
] as const;

type Stage = (typeof STAGES)[number];

type ExcelTemplateRow = {
  id: number;
  stage: Stage;
  status: 'DRAFT' | 'PUBLISHED';
  originalFilename: string;
  mimeType: string;
  sizeBytes: number;
  mappingComplete: boolean;
  isActive: boolean;
  label: string | null;
  createdAt: string;
  updatedAt: string;
  createdByUser: { id: number; userDisplayName: string | null; userEmail: string | null } | null;
};

type MappingPayload = {
  templateId: number;
  stage: Stage;
  mapping: Record<string, unknown> | null;
  mappingComplete: boolean;
  allowedScalarFields: string[];
  allowedLineFields: string[];
};

const STAGE_LABELS: Record<Stage, string> = {
  REQUISITION: 'Requisition',
  RFQ: 'RFQ',
  QUOTATION: 'Quotation',
  EVALUATION: 'Evaluation',
  ORDER: 'Order',
  DELIVERY: 'Delivery',
  INVOICE: 'Invoice',
};

function stageBadgeClass(stage: Stage): string {
  const map: Record<Stage, string> = {
    REQUISITION: 'bg-slate-500/15 text-slate-800 dark:text-slate-200 ring-1 ring-slate-500/25',
    RFQ: 'bg-sky-500/15 text-sky-900 dark:text-sky-100 ring-1 ring-sky-500/25',
    QUOTATION: 'bg-violet-500/15 text-violet-900 dark:text-violet-100 ring-1 ring-violet-500/25',
    EVALUATION: 'bg-amber-500/15 text-amber-900 dark:text-amber-100 ring-1 ring-amber-500/25',
    ORDER: 'bg-emerald-500/15 text-emerald-900 dark:text-emerald-100 ring-1 ring-emerald-500/25',
    DELIVERY: 'bg-cyan-500/15 text-cyan-900 dark:text-cyan-100 ring-1 ring-cyan-500/25',
    INVOICE: 'bg-rose-500/15 text-rose-900 dark:text-rose-100 ring-1 ring-rose-500/25',
  };
  return map[stage] ?? map.RFQ;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatShortDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

const XLSX_ACCEPT = '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function isAcceptableXlsx(file: File): boolean {
  if (file.name.toLowerCase().endsWith('.xlsx')) return true;
  return file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
}

const List: React.FC = () => {
  const { colors } = useTheme();
  const { showError, showSuccess } = useNotification();
  const [stageFilter, setStageFilter] = useState<string>('');
  const [uploadStage, setUploadStage] = useState<Stage>('RFQ');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const [mappingModalId, setMappingModalId] = useState<number | null>(null);
  const [savingMapping, setSavingMapping] = useState(false);
  const [previewMappingBusy, setPreviewMappingBusy] = useState(false);
  const mappingEditorRef = useRef<MappingEditorHandle>(null);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [uploadDragActive, setUploadDragActive] = useState(false);
  const uploadDragDepth = useRef(0);
  const uploadFileInputRef = useRef<HTMLInputElement>(null);

  const listKey = useMemo(
    () =>
      stageFilter
        ? `/excel-document-templates?stage=${encodeURIComponent(stageFilter)}`
        : '/excel-document-templates',
    [stageFilter],
  );

  const { data, error, isLoading, mutate } = useSWR<ExcelTemplateRow[]>(listKey, fetcher, {
    revalidateOnFocus: true,
  });

  const {
    data: mappingPayload,
    isLoading: mappingLoading,
    mutate: mutateMapping,
  } = useSWR<MappingPayload>(
    mappingModalId ? `/excel-document-templates/${mappingModalId}/mapping` : null,
    fetcher,
  );

  const onUploadDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    uploadDragDepth.current += 1;
    const typeList = Array.from(e.dataTransfer?.types ?? []);
    if (typeList.includes('Files')) setUploadDragActive(true);
  }, []);

  const onUploadDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    uploadDragDepth.current -= 1;
    if (uploadDragDepth.current <= 0) {
      uploadDragDepth.current = 0;
      setUploadDragActive(false);
    }
  }, []);

  const onUploadDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const onUploadDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      uploadDragDepth.current = 0;
      setUploadDragActive(false);
      const file = e.dataTransfer.files?.[0];
      if (!file) return;
      if (isAcceptableXlsx(file)) {
        setUploadFile(file);
      } else {
        showError('Only .xlsx Excel files are accepted.');
      }
    },
    [showError],
  );

  const openFilePicker = useCallback(() => {
    uploadFileInputRef.current?.click();
  }, []);

  const handleUpload = async () => {
    if (!uploadFile) {
      showError('Choose an .xlsx file.');
      return;
    }
    setUploading(true);
    try {
      await uploadExcelDocumentTemplate(uploadStage, uploadFile);
      showSuccess('Template uploaded.');
      setUploadFile(null);
      await mutate();
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveMapping = async () => {
    if (!mappingModalId) return;
    const parsed = mappingEditorRef.current?.buildMapping();
    if (!parsed) {
      showError('Map at least one header cell (sheet + cell) or one line table with sheet, row, and columns.');
      return;
    }
    setSavingMapping(true);
    try {
      await updateExcelDocumentTemplateMapping(mappingModalId, parsed);
      showSuccess('Mapping saved. You can preview sample data from this dialog.');
      await mutate();
      await mutateMapping();
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Could not save mapping.');
    } finally {
      setSavingMapping(false);
    }
  };

  const handlePublish = async (id: number) => {
    try {
      await publishExcelDocumentTemplate(id);
      showSuccess('Template published.');
      await mutate();
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Publish failed.');
    }
  };

  const handleActivate = async (id: number) => {
    try {
      await activateExcelDocumentTemplate(id);
      showSuccess('Template activated for this stage.');
      await mutate();
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Activate failed.');
    }
  };

  const handleDelete = async () => {
    if (deleteId == null) return;
    setDeleting(true);
    try {
      await deleteExcelDocumentTemplate(deleteId);
      showSuccess('Template deleted.');
      setDeleteId(null);
      await mutate();
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  const previewSheets = useCallback(
    async (id: number) => {
      try {
        const res = await api.get<{ sheets: string[] }>(`/excel-document-templates/${id}/preview-sheets`);
        const names = res.data.sheets?.join(', ') || '(none)';
        showSuccess(`Sheets: ${names}`);
      } catch (e) {
        showError(e instanceof Error ? e.message : 'Could not read sheets.');
      }
    },
    [showError, showSuccess],
  );

  const downloadOriginal = async (id: number) => {
    try {
      await downloadExcelDocumentTemplateOriginal(id);
      showSuccess('Download started.');
    } catch {
      showError('Download failed.');
    }
  };

  const downloadPreview = async (id: number) => {
    try {
      await downloadExcelDocumentTemplatePreviewFilled(id);
      showSuccess('Preview download started.');
    } catch {
      showError('Preview download failed.');
    }
  };

  const handlePreviewFromMappingModal = async () => {
    if (!mappingModalId) return;
    setPreviewMappingBusy(true);
    try {
      await downloadExcelDocumentTemplatePreviewFilled(mappingModalId);
      showSuccess('Sample preview download started.');
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Preview download failed. Save a valid mapping first.');
    } finally {
      setPreviewMappingBusy(false);
    }
  };

  const rows = data ?? [];

  return (
    <div
      className={cn(
        'min-h-[60vh] w-full min-w-0 px-4 sm:px-6 lg:px-8 py-8 space-y-10',
        colors.background,
      )}
    >
      {/* Hero */}
      <header
        className={cn(
          'relative overflow-hidden rounded-2xl border shadow-sm',
          colors.border,
          colors.surface,
        )}
      >
        <div
          className="h-1.5 w-full bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500"
          aria-hidden
        />
        <div className="p-6 sm:p-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex gap-4 max-w-2xl">
              <div
                className={cn(
                  'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl',
                  'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 ring-1 ring-indigo-500/20',
                )}
              >
                <TableIcon className="w-6 h-6" aria-hidden />
              </div>
              <div>
                <p className={cn('text-xs font-semibold uppercase tracking-wider', colors.textMuted)}>
                  Supplies · Administration
                </p>
                <h1 className={cn('mt-1 text-2xl sm:text-3xl font-bold tracking-tight', colors.text)}>
                  Excel export templates
                </h1>
                <p className={cn('mt-2 text-sm leading-relaxed', colors.textSecondary)}>
                  Upload an <strong className={cn('font-medium', colors.text)}>.xlsx</strong> per workflow stage,
                  map cells to system fields, then publish and activate the template staff exports use (e.g. RFQ
                  downloads).
                </p>
                <ol
                  className={cn(
                    'mt-4 grid gap-2 text-xs sm:text-sm sm:grid-cols-2 lg:grid-cols-4',
                    colors.textSecondary,
                  )}
                >
                  {[
                    '1 · Upload file',
                    '2 · Map cells',
                    '3 · Publish',
                    '4 · Activate for stage',
                  ].map((step) => (
                    <li
                      key={step}
                      className={cn(
                        'rounded-lg px-3 py-2 border',
                        colors.border,
                        colors.surfaceHover,
                      )}
                    >
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Upload */}
      <section aria-labelledby="upload-heading">
        <h2 id="upload-heading" className="sr-only">
          Upload template
        </h2>
        <input
          ref={uploadFileInputRef}
          type="file"
          accept={XLSX_ACCEPT}
          className="sr-only"
          tabIndex={-1}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f && isAcceptableXlsx(f)) {
              setUploadFile(f);
            } else if (f) {
              showError('Only .xlsx Excel files are accepted.');
            }
            e.target.value = '';
          }}
        />

        <div
          className={cn(
            'rounded-2xl border shadow-md overflow-hidden',
            colors.border,
            colors.surface,
          )}
        >
          <div
            className={cn(
              'flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between px-5 py-4 border-b',
              colors.border,
              colors.surfaceHover,
            )}
          >
            <div className="flex items-start gap-3">
              <div
                className={cn(
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
                  'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
                )}
              >
                <UploadCloudIcon className="w-5 h-5" aria-hidden />
              </div>
              <div>
                <p className={cn('text-base font-semibold', colors.text)}>New template</p>
                <p className={cn('text-sm mt-0.5 max-w-xl', colors.textSecondary)}>
                  Pick a workflow stage, then drop an <strong className={cn('font-medium', colors.text)}>.xlsx</strong>{' '}
                  file or browse. Size limits follow the server. Next, use{' '}
                  <strong className={cn('font-medium', colors.text)}>mapping</strong> to connect cells to fields.
                </p>
              </div>
            </div>
            <label className="flex flex-col gap-1.5 w-full sm:w-auto sm:min-w-[13rem]">
              <span className={cn('text-xs font-medium', colors.textSecondary)}>Workflow stage</span>
              <select
                value={uploadStage}
                onChange={(e) => setUploadStage(e.target.value as Stage)}
                className={cn(
                  'rounded-xl border px-3 py-2.5 text-sm shadow-sm w-full',
                  colors.border,
                  colors.surface,
                  colors.text,
                )}
              >
                {STAGES.map((s) => (
                  <option key={s} value={s}>
                    {STAGE_LABELS[s]}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div
            role="button"
            tabIndex={0}
            aria-label="Drop zone for Excel template file"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openFilePicker();
              }
            }}
            onDragEnter={onUploadDragEnter}
            onDragLeave={onUploadDragLeave}
            onDragOver={onUploadDragOver}
            onDrop={onUploadDrop}
            onClick={(e) => {
              if ((e.target as HTMLElement).closest('[data-no-dropzone-click]')) return;
              openFilePicker();
            }}
            className={cn(
              'relative min-h-[220px] px-6 py-10 cursor-pointer transition-all duration-200',
              'bg-gradient-to-b from-transparent via-indigo-500/[0.02] to-indigo-500/[0.06]',
              'dark:via-indigo-400/[0.03] dark:to-indigo-400/[0.07]',
              uploadDragActive
                ? 'ring-2 ring-inset ring-indigo-500 border-indigo-500/40 dark:ring-indigo-400 scale-[1.002]'
                : cn('border-2 border-dashed border-transparent', 'hover:border-indigo-400/50 dark:hover:border-indigo-500/40'),
            )}
          >
            <div className="pointer-events-none absolute inset-0 rounded-[inherit] border-2 border-dashed border-indigo-300/60 dark:border-indigo-500/35" />

            <div className="relative flex flex-col items-center justify-center text-center gap-3 max-w-lg mx-auto">
              <div
                className={cn(
                  'flex h-16 w-16 items-center justify-center rounded-2xl transition-transform duration-200',
                  uploadDragActive
                    ? 'bg-indigo-500/25 text-indigo-700 dark:text-indigo-200 scale-110'
                    : 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
                )}
              >
                <UploadCloudIcon className="w-9 h-9" aria-hidden />
              </div>
              <div>
                <p className={cn('text-lg font-semibold', colors.text)}>
                  {uploadDragActive ? 'Release to upload' : 'Drop your Excel template here'}
                </p>
                <p className={cn('text-sm mt-1', colors.textSecondary)}>
                  <button
                    type="button"
                    data-no-dropzone-click
                    onClick={(e) => {
                      e.stopPropagation();
                      openFilePicker();
                    }}
                    className={cn(
                      'font-semibold underline decoration-indigo-500/60 underline-offset-2 hover:decoration-indigo-600',
                      colors.primaryFg,
                    )}
                  >
                    Browse files
                  </button>
                  <span className="mx-1.5 opacity-50" aria-hidden>
                    ·
                  </span>
                  <span>.xlsx only</span>
                </p>
              </div>
            </div>
          </div>

          <div
            className={cn(
              'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-5 py-4 border-t',
              colors.border,
              colors.surfaceHover,
            )}
          >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <DocumentTextIcon className="w-5 h-5 shrink-0 text-indigo-600/80 dark:text-indigo-400/90" aria-hidden />
                <div className="min-w-0">
                  {uploadFile ? (
                    <>
                      <p className={cn('text-sm font-medium truncate', colors.text)} title={uploadFile.name}>
                        {uploadFile.name}
                      </p>
                      <p className={cn('text-xs', colors.textMuted)}>
                        {formatBytes(uploadFile.size)}
                        <span className="mx-2 opacity-40">·</span>
                        Ready to upload for <strong className={cn('font-medium', colors.textSecondary)}>{STAGE_LABELS[uploadStage]}</strong>
                      </p>
                    </>
                  ) : (
                    <p className={cn('text-sm', colors.textMuted)}>No file selected yet.</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0 justify-end">
                {uploadFile && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    data-no-dropzone-click
                    onClick={(e) => {
                      e.stopPropagation();
                      setUploadFile(null);
                      if (uploadFileInputRef.current) uploadFileInputRef.current.value = '';
                    }}
                    className="inline-flex items-center gap-1.5"
                  >
                    <XIcon className="w-4 h-4" aria-hidden />
                    Clear
                  </Button>
                )}
                <Button
                  type="button"
                  data-no-dropzone-click
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUpload();
                  }}
                  loading={uploading}
                  disabled={uploading || !uploadFile}
                  className="inline-flex items-center justify-center gap-2 px-6 rounded-xl shadow-sm"
                >
                  <UploadCloudIcon className="w-4 h-4 shrink-0" />
                  Upload template
                </Button>
              </div>
            </div>
        </div>
      </section>

      {/* Library */}
      <section className="space-y-4" aria-labelledby="library-heading">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 id="library-heading" className={cn('text-lg font-semibold', colors.text)}>
              Template library
            </h2>
            <p className={cn('text-sm mt-0.5', colors.textSecondary)}>
              Drafts stay internal; only published templates can be activated for live exports.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span
              className={cn(
                'inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium border',
                colors.border,
                colors.surfaceHover,
                colors.textSecondary,
              )}
            >
              <FilterIcon className="w-3.5 h-3.5 opacity-80" aria-hidden />
              {rows.length} template{rows.length !== 1 ? 's' : ''}
            </span>
            <label className="inline-flex items-center gap-2">
              <span className={cn('text-xs font-medium', colors.textSecondary)}>Stage</span>
              <select
                value={stageFilter}
                onChange={(e) => setStageFilter(e.target.value)}
                className={cn(
                  'rounded-xl border px-3 py-2 text-sm shadow-sm min-w-[10.5rem]',
                  colors.border,
                  colors.surfaceHover,
                  colors.text,
                )}
              >
                <option value="">All stages</option>
                {STAGES.map((s) => (
                  <option key={s} value={s}>
                    {STAGE_LABELS[s]}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {error && (
          <div
            className="rounded-xl border border-rose-200 bg-rose-50/90 px-4 py-3 text-sm text-rose-900 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-100 flex gap-2 items-start"
            role="alert"
          >
            <InfoIcon className="w-5 h-5 shrink-0 text-rose-600 dark:text-rose-400" aria-hidden />
            <span>Could not load templates. Refresh the page or check your connection.</span>
          </div>
        )}

        <div
          className={cn(
            'rounded-2xl border shadow-sm overflow-hidden',
            colors.border,
            colors.surface,
          )}
        >
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
                  <th className="px-4 py-3 font-semibold">Template</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Stage</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Status</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Mapping</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Live</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap hidden md:table-cell">Updated</th>
                  <th className="px-4 py-3 font-semibold hidden lg:table-cell">Uploaded by</th>
                  <th className="px-4 py-3 font-semibold text-right w-[1%]">Actions</th>
                </tr>
              </thead>
              <tbody className={cn('divide-y', colors.divide)}>
                {isLoading && (
                  <tr>
                    <td colSpan={8} className={cn('px-4 py-16 text-center', colors.textSecondary)}>
                      <span className="inline-flex items-center gap-2">
                        <span
                          className="h-4 w-4 rounded-full border-2 border-current border-t-transparent animate-spin opacity-70"
                          aria-hidden
                        />
                        Loading templates…
                      </span>
                    </td>
                  </tr>
                )}
                {!isLoading && rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-16">
                      <div className="flex flex-col items-center justify-center text-center max-w-sm mx-auto gap-3">
                        <div
                          className={cn(
                            'flex h-14 w-14 items-center justify-center rounded-2xl',
                            'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
                          )}
                        >
                          <DocumentTextIcon className="w-7 h-7" aria-hidden />
                        </div>
                        <p className={cn('font-medium', colors.text)}>No templates yet</p>
                        <p className={cn('text-sm', colors.textSecondary)}>
                          Upload an .xlsx above to create your first export template for this environment.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  rows.map((row) => (
                    <tr
                      key={row.id}
                      className={cn(
                        'transition-colors',
                        colors.interactiveHover,
                        row.isActive &&
                          'bg-indigo-500/[0.05] ring-1 ring-inset ring-indigo-500/20 dark:bg-indigo-400/[0.07] dark:ring-indigo-400/25',
                      )}
                    >
                      <td className="px-4 py-3 align-top">
                        <div className="flex gap-3 min-w-0">
                          <div
                            className={cn(
                              'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                              colors.surfaceHover,
                              colors.border,
                              'border',
                            )}
                          >
                            <DocumentTextIcon className="w-4 h-4 opacity-80" aria-hidden />
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium truncate" title={row.originalFilename}>
                              {row.originalFilename}
                            </div>
                            <div className={cn('text-xs mt-0.5', colors.textMuted)}>
                              #{row.id} · {formatBytes(row.sizeBytes)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-middle whitespace-nowrap">
                        <span
                          className={cn(
                            'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                            stageBadgeClass(row.stage),
                          )}
                        >
                          {STAGE_LABELS[row.stage]}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-middle whitespace-nowrap">
                        <span
                          className={cn(
                            'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
                            row.status === 'DRAFT'
                              ? 'bg-amber-500/15 text-amber-900 dark:text-amber-100 ring-1 ring-amber-500/30'
                              : 'bg-emerald-500/15 text-emerald-900 dark:text-emerald-100 ring-1 ring-emerald-500/30',
                          )}
                        >
                          {row.status === 'DRAFT' ? 'Draft' : 'Published'}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-middle whitespace-nowrap">
                        {row.mappingComplete ? (
                          <span className="inline-flex items-center gap-1.5 text-emerald-700 dark:text-emerald-300">
                            <CheckCircleIcon className="w-4 h-4 shrink-0" aria-hidden />
                            <span className="text-xs font-medium">Ready</span>
                          </span>
                        ) : (
                          <span className={cn('text-xs', colors.textMuted)}>Incomplete</span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-middle whitespace-nowrap">
                        {row.isActive ? (
                          <span className="inline-flex items-center gap-1.5 text-indigo-700 dark:text-indigo-300">
                            <StarIcon filled className="w-4 h-4 shrink-0" aria-hidden />
                            <span className="text-xs font-medium">Active</span>
                          </span>
                        ) : (
                          <span className={cn('text-xs', colors.textMuted)}>—</span>
                        )}
                      </td>
                      <td className={cn('px-4 py-3 align-middle whitespace-nowrap hidden md:table-cell', colors.textSecondary)}>
                        {formatShortDate(row.updatedAt)}
                      </td>
                      <td className={cn('px-4 py-3 align-middle max-w-[9rem] truncate text-xs hidden lg:table-cell', colors.textSecondary)} title={row.createdByUser?.userEmail ?? ''}>
                        {row.createdByUser?.userDisplayName || row.createdByUser?.userEmail || '—'}
                      </td>
                      <td className="px-4 py-3 text-right align-middle w-[1%]">
                        <div
                          className="hidden md:inline-flex flex-nowrap items-center justify-end gap-0.5"
                          role="group"
                          aria-label={`Actions for template ${row.id}`}
                        >
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            title="Edit cell mapping"
                            aria-label="Edit cell mapping"
                            onClick={() => setMappingModalId(row.id)}
                          >
                            <PencilIcon className="w-4 h-4" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            title="Download original file"
                            aria-label="Download original file"
                            onClick={() => downloadOriginal(row.id)}
                          >
                            <DownloadIcon className="w-4 h-4" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            title="Show sheet names"
                            aria-label="Show sheet names"
                            onClick={() => previewSheets(row.id)}
                          >
                            <ViewColumnsIcon className="w-4 h-4" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            title="Download preview with sample data"
                            aria-label="Download preview with sample data"
                            onClick={() => downloadPreview(row.id)}
                            disabled={!row.mappingComplete}
                          >
                            <EyeIcon className="w-4 h-4" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="secondary"
                            title="Publish template"
                            aria-label="Publish template"
                            onClick={() => handlePublish(row.id)}
                            disabled={row.status !== 'DRAFT' || !row.mappingComplete}
                          >
                            <CheckCircleIcon className="w-4 h-4" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="accent"
                            title="Activate for this stage"
                            aria-label="Activate for this stage"
                            onClick={() => handleActivate(row.id)}
                            disabled={row.status !== 'PUBLISHED' || !row.mappingComplete}
                          >
                            <StarIcon className="w-4 h-4" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="danger"
                            title="Delete template"
                            aria-label="Delete template"
                            onClick={() => setDeleteId(row.id)}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </Button>
                        </div>

                        <details className="md:hidden relative inline-block text-left">
                          <summary
                            className={cn(
                              'list-none inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border text-lg font-bold leading-none [&::-webkit-details-marker]:hidden',
                              colors.border,
                              colors.surfaceHover,
                            )}
                            aria-label="Row actions menu"
                          >
                            <span aria-hidden>⋯</span>
                          </summary>
                          <div
                            className={cn(
                              'absolute right-0 z-30 mt-1 min-w-[12.5rem] max-w-[calc(100vw-2rem)] rounded-xl border py-1 shadow-lg',
                              colors.border,
                              colors.surface,
                            )}
                          >
                            <button
                              type="button"
                              className={cn(
                                'flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm',
                                colors.interactiveHover,
                              )}
                              onClick={(e) => {
                                (e.currentTarget as HTMLElement).closest('details')?.removeAttribute('open');
                                setMappingModalId(row.id);
                              }}
                            >
                              <PencilIcon className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
                              Edit mapping
                            </button>
                            <button
                              type="button"
                              className={cn(
                                'flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm',
                                colors.interactiveHover,
                              )}
                              onClick={(e) => {
                                (e.currentTarget as HTMLElement).closest('details')?.removeAttribute('open');
                                void downloadOriginal(row.id);
                              }}
                            >
                              <DownloadIcon className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
                              Download original
                            </button>
                            <button
                              type="button"
                              className={cn(
                                'flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm',
                                colors.interactiveHover,
                              )}
                              onClick={(e) => {
                                (e.currentTarget as HTMLElement).closest('details')?.removeAttribute('open');
                                void previewSheets(row.id);
                              }}
                            >
                              <ViewColumnsIcon className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
                              Sheet names
                            </button>
                            <button
                              type="button"
                              disabled={!row.mappingComplete}
                              className={cn(
                                'flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm disabled:opacity-40',
                                colors.interactiveHover,
                              )}
                              onClick={(e) => {
                                (e.currentTarget as HTMLElement).closest('details')?.removeAttribute('open');
                                void downloadPreview(row.id);
                              }}
                            >
                              <EyeIcon className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
                              Preview sample
                            </button>
                            <button
                              type="button"
                              disabled={row.status !== 'DRAFT' || !row.mappingComplete}
                              className={cn(
                                'flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm disabled:opacity-40',
                                colors.interactiveHover,
                              )}
                              onClick={(e) => {
                                (e.currentTarget as HTMLElement).closest('details')?.removeAttribute('open');
                                void handlePublish(row.id);
                              }}
                            >
                              <CheckCircleIcon className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
                              Publish
                            </button>
                            <button
                              type="button"
                              disabled={row.status !== 'PUBLISHED' || !row.mappingComplete}
                              className={cn(
                                'flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm disabled:opacity-40',
                                colors.interactiveHover,
                              )}
                              onClick={(e) => {
                                (e.currentTarget as HTMLElement).closest('details')?.removeAttribute('open');
                                void handleActivate(row.id);
                              }}
                            >
                              <StarIcon className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
                              Set active
                            </button>
                            <button
                              type="button"
                              className={cn(
                                'flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-rose-700 dark:text-rose-300',
                                colors.interactiveHover,
                              )}
                              onClick={(e) => {
                                (e.currentTarget as HTMLElement).closest('details')?.removeAttribute('open');
                                setDeleteId(row.id);
                              }}
                            >
                              <TrashIcon className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
                              Delete
                            </button>
                          </div>
                        </details>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className={cn('text-xs flex flex-wrap items-start gap-2', colors.textMuted)}>
          <InfoIcon className="w-4 h-4 shrink-0 mt-0.5 opacity-70" aria-hidden />
          <span>
            <strong className={cn('font-medium', colors.textSecondary)}>Actions:</strong> pencil = mapping, arrow =
            original file, columns = sheet names, eye = sample preview, check = publish, star = set active for stage,
            trash = delete. On narrow screens, open <strong className={cn('font-medium', colors.textSecondary)}>⋯</strong>{' '}
            for the same menu.
          </span>
        </p>
      </section>

      <Modal isOpen={mappingModalId != null} onClose={() => setMappingModalId(null)} size="xl">
        <ModalHeader>
          <ModalTitle>
            <span className="inline-flex items-center gap-2">
              <TableIcon className="w-5 h-5 shrink-0" aria-hidden />
              Template cell mapping
            </span>
          </ModalTitle>
        </ModalHeader>
        <ModalContent>
          {mappingLoading && (
            <p className={cn('text-sm', colors.textSecondary)}>Loading mapping…</p>
          )}
          {!mappingLoading && mappingPayload && (
            <>
              <MappingEditor
                ref={mappingEditorRef}
                key={`tmpl-${mappingModalId}-${mappingPayload.templateId}`}
                allowedScalarFields={mappingPayload.allowedScalarFields}
                allowedLineFields={mappingPayload.allowedLineFields}
                existingMapping={mappingPayload.mapping}
                disabled={mappingLoading}
              />
              <p className={cn('mt-6 text-xs leading-relaxed border-t pt-4', colors.border, colors.textMuted)}>
                <strong className={cn('font-medium', colors.textSecondary)}>Preview</strong> fills the workbook using
                the <strong className={cn('font-medium', colors.textSecondary)}>last saved</strong> mapping. Save your
                changes, then use Preview sample below.
              </p>
            </>
          )}
          {!mappingLoading && !mappingPayload && mappingModalId != null && (
            <p className={cn('text-sm text-amber-700 dark:text-amber-300')}>
              Could not load mapping metadata. Try again or check your connection.
            </p>
          )}
        </ModalContent>
        <ModalFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end sm:items-center">
          <Button
            type="button"
            variant="ghost"
            onClick={() => setMappingModalId(null)}
            className="inline-flex items-center justify-center gap-2 w-full sm:w-auto"
          >
            <XIcon className="w-4 h-4 shrink-0" aria-hidden />
            Cancel
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handlePreviewFromMappingModal}
            loading={previewMappingBusy}
            disabled={mappingLoading || !mappingPayload || previewMappingBusy || savingMapping}
            className="inline-flex items-center justify-center gap-2 w-full sm:w-auto"
          >
            <EyeIcon className="w-4 h-4 shrink-0" aria-hidden />
            Preview sample
          </Button>
          <Button
            type="button"
            onClick={handleSaveMapping}
            loading={savingMapping}
            disabled={mappingLoading || !mappingPayload || previewMappingBusy}
            className="inline-flex items-center justify-center gap-2 w-full sm:w-auto"
          >
            <CheckIcon className="w-4 h-4 shrink-0" aria-hidden />
            Save mapping
          </Button>
        </ModalFooter>
      </Modal>

      <ConfirmationDialog
        isOpen={deleteId != null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete template"
        message="Delete this template and its stored file? This cannot be undone."
        confirmText="Delete"
        confirmVariant="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default List;
